import { useState, useMemo, useCallback, useEffect } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  InputAdornment,
  CircularProgress,
  IconButton,
} from "@mui/material";
import GlobalIcon from "../../components/GlobalIcon/GlobalIcon";
import { memberClientIdSet } from "../../utils/basketMembers";
import styles from "./BasketManageSection.module.css";

const PICKER_PAGE_SIZE = 200;

function clientRowLabel(c) {
  const code = c.broker_client_code ?? "—";
  return `${code}`;
}

export default function BasketManageSection({
  basket,
  members,
  allClients,
  clientsLoading,
  isAdding,
  removingClientId,
  isDeletingBasket,
  onAddClients,
  onRemoveMember,
  onDeleteBasket,
}) {
  const [addOpen, setAddOpen] = useState(false);
  const [deleteBasketOpen, setDeleteBasketOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState("");
  const [selectedToAdd, setSelectedToAdd] = useState(() => new Set());
  const [localError, setLocalError] = useState(null);
  const [pickerListCap, setPickerListCap] = useState(PICKER_PAGE_SIZE);

  const inBasketIds = useMemo(() => memberClientIdSet(members), [members]);

  const availableClients = useMemo(() => {
    const list = Array.isArray(allClients) ? allClients : [];
    return list.filter((c) => c.id != null && !inBasketIds.has(Number(c.id)));
  }, [allClients, inBasketIds]);

  const filteredAvailable = useMemo(() => {
    const q = pickerQuery.trim().toLowerCase();
    if (!q) return availableClients;
    return availableClients.filter((c) => {
      const code = String(c.broker_client_code ?? "").toLowerCase();
      const idStr = String(c.id ?? "");
      return code.includes(q) || idStr.includes(q);
    });
  }, [availableClients, pickerQuery]);

  const visiblePickerRows = useMemo(
    () => filteredAvailable.slice(0, pickerListCap),
    [filteredAvailable, pickerListCap]
  );

  useEffect(() => {
    setPickerListCap(PICKER_PAGE_SIZE);
  }, [pickerQuery, addOpen]);

  const openAdd = useCallback(() => {
    setLocalError(null);
    setPickerQuery("");
    setSelectedToAdd(new Set());
    setAddOpen(true);
  }, []);

  const closeAdd = useCallback(() => {
    setAddOpen(false);
    setPickerQuery("");
    setSelectedToAdd(new Set());
    setLocalError(null);
    setPickerListCap(PICKER_PAGE_SIZE);
  }, []);

  const togglePick = useCallback((clientId) => {
    const id = Number(clientId);
    setSelectedToAdd((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const togglePickAllInView = useCallback(() => {
    const ids = visiblePickerRows.map((c) => Number(c.id)).filter((n) => !Number.isNaN(n));
    setSelectedToAdd((prev) => {
      const allSelected = ids.length > 0 && ids.every((id) => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      }
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });
  }, [visiblePickerRows]);

  const handleConfirmAdd = useCallback(async () => {
    const ids = [...selectedToAdd];
    if (ids.length === 0) return;
    setLocalError(null);
    try {
      await onAddClients(ids);
      closeAdd();
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        (typeof err?.response?.data === "string" ? err.response.data : null) ||
        err?.message ||
        "Failed to add clients";
      setLocalError(msg);
    }
  }, [selectedToAdd, onAddClients, closeAdd]);

  const handleRemove = useCallback(
    async (clientId) => {
      setLocalError(null);
      try {
        await onRemoveMember(clientId);
      } catch (err) {
        const msg =
          err?.response?.data?.detail ||
          err?.message ||
          "Failed to remove client from basket";
        setLocalError(msg);
      }
    },
    [onRemoveMember]
  );

  const handleDeleteBasket = useCallback(async () => {
    setLocalError(null);
    try {
      await onDeleteBasket();
      setDeleteBasketOpen(false);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to delete basket";
      setLocalError(msg);
    }
  }, [onDeleteBasket]);

  const busy = isAdding || isDeletingBasket || removingClientId != null;

  return (
    <div className={styles.manage}>
      {localError && !addOpen ? (
        <Alert severity="error" onClose={() => setLocalError(null)}>
          {localError}
        </Alert>
      ) : null}

      <div className={styles.manageHeader}>
        <div>
          <h3 className={styles.manageTitle}>
            Clients in this basket
            <span className={styles.manageTitleCount}> ({members.length})</span>
          </h3>
          <p className={styles.manageSubtitle}>
            Add or remove trading accounts linked to &ldquo;{basket.basket_name}&rdquo;. Scroll if the list is long.
          </p>
        </div>
        <div className={styles.manageActions}>
          <Button
            type="button"
            variant="contained"
            className={styles.addBtn}
            onClick={openAdd}
            disabled={busy || clientsLoading}
            startIcon={<GlobalIcon iconName="AddIcon" style={{ width: 18, height: 18 }} />}
          >
            Add clients
          </Button>
          <Button
            type="button"
            variant="outlined"
            className={styles.deleteBasketBtn}
            onClick={() => { setLocalError(null); setDeleteBasketOpen(true); }}
            disabled={busy}
            startIcon={<GlobalIcon iconName="DeleteTrashIcon" style={{ width: 18, height: 18 }} />}
          >
            Delete basket
          </Button>
        </div>
      </div>

      {members.length === 0 ? (
        <p className={styles.emptyMembers}>No clients in this basket yet. Use Add clients to include accounts.</p>
      ) : (
        <div className={styles.chipsScroll}>
          <div className={styles.chipsWrap}>
            {members.map((m) => {
              const removing =
                m.clientId != null &&
                removingClientId != null &&
                String(removingClientId) === String(m.clientId);
              return (
                <span
                  key={m.clientId != null ? `c-${m.clientId}` : `code-${m.broker_client_code}`}
                  className={styles.memberChip}
                >
                  <span className={styles.chipLabel} title={m.clientId != null ? `Client ID ${m.clientId}` : undefined}>
                    {m.broker_client_code}
                  </span>
                  <button
                    type="button"
                    className={styles.chipRemove}
                    aria-label={`Remove ${m.broker_client_code} from basket`}
                    disabled={!m.canRemove || busy || removing}
                    title={!m.canRemove ? "Client id unavailable for this row" : "Remove from basket"}
                    onClick={() => m.clientId != null && handleRemove(m.clientId)}
                  >
                    {removing ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <GlobalIcon iconName="RoundedCrossIcon" style={{ width: 14, height: 14 }} />
                    )}
                  </button>
                </span>
              );
            })}
          </div>
        </div>
      )}

      <Dialog
        open={addOpen}
        onClose={busy ? undefined : closeAdd}
        fullWidth
        maxWidth="sm"
        scroll="paper"
        aria-labelledby="settings-add-clients-dialog-title"
        PaperProps={{
          elevation: 0,
          className: styles.addDialogPaper,
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(15, 23, 42, 0.48)",
              backdropFilter: "blur(6px)",
            },
          },
        }}
      >
        <DialogTitle id="settings-add-clients-dialog-title" component="div" className={styles.addDialogTitleRoot}>
          <div className={styles.addDialogHeader}>
            <div className={styles.addDialogHeaderMain}>
              <span className={styles.addDialogEyebrow}>Basket</span>
              <h2 className={styles.addDialogHeading}>Add clients</h2>
              <p className={styles.addDialogBasketLine}>
                to <strong>{basket.basket_name}</strong>
              </p>
              {availableClients.length > 0 ? (
                <p className={styles.addDialogStat}>
                  <span className={styles.addDialogStatDot} aria-hidden />
                  {availableClients.length} account{availableClients.length !== 1 ? "s" : ""} available to add
                </p>
              ) : null}
            </div>
            <IconButton
              type="button"
              onClick={closeAdd}
              disabled={isAdding}
              className={styles.addDialogCloseBtn}
              aria-label="Close"
              size="small"
            >
              <GlobalIcon iconName="RoundedCrossIcon" style={{ width: 16, height: 16 }} />
            </IconButton>
          </div>
        </DialogTitle>

        <DialogContent className={styles.addDialogBody}>
          <div className={styles.addDialogSearchWrap}>
            <TextField
              className={styles.addDialogSearch}
              placeholder="Search by client code or ID…"
              value={pickerQuery}
              onChange={(e) => setPickerQuery(e.target.value)}
              fullWidth
              size="small"
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <GlobalIcon iconName="SearchIcon" style={{ width: 20, height: 20, opacity: 0.45 }} />
                  </InputAdornment>
                ),
              }}
            />
          </div>

          {localError && addOpen ? (
            <Alert
              severity="error"
              className={styles.addDialogAlert}
              onClose={() => setLocalError(null)}
              sx={{ borderRadius: "10px", fontFamily: "Inter, sans-serif" }}
            >
              {localError}
            </Alert>
          ) : null}

          {availableClients.length === 0 ? (
            <div className={styles.addDialogEmpty}>
              <p className={styles.addDialogEmptyTitle}>No accounts to add</p>
              <p className={styles.addDialogEmptyText}>
                Everyone is already in this basket, or the client list did not load from the API.
              </p>
            </div>
          ) : (
            <>
              {filteredAvailable.length > PICKER_PAGE_SIZE && !pickerQuery.trim() ? (
                <div className={styles.addDialogHintBar}>
                  <span className={styles.addDialogHintIcon} aria-hidden />
                  Large directory: showing {pickerListCap} of {filteredAvailable.length}. Use search or load more.
                </div>
              ) : null}

              <div className={styles.addDialogListShell}>
                <div className={styles.selectAllBar}>
                  <FormControlLabel
                    className={styles.selectAllLabel}
                    control={(
                      <Checkbox
                        size="small"
                        sx={{
                          color: "var(--text-muted)",
                          "&.Mui-checked": { color: "var(--accent)" },
                          "&.MuiCheckbox-indeterminate": { color: "var(--accent)" },
                        }}
                        checked={
                          visiblePickerRows.length > 0 &&
                          visiblePickerRows.every((c) => selectedToAdd.has(Number(c.id)))
                        }
                        indeterminate={
                          visiblePickerRows.some((c) => selectedToAdd.has(Number(c.id))) &&
                          !visiblePickerRows.every((c) => selectedToAdd.has(Number(c.id)))
                        }
                        onChange={togglePickAllInView}
                      />
                    )}
                    label="Select all on this page"
                  />
                </div>
                <div className={styles.dialogList}>
                  {visiblePickerRows.map((c) => {
                    const id = Number(c.id);
                    return (
                      <div key={id} className={styles.dialogRow}>
                        <Checkbox
                          size="small"
                          sx={{
                            color: "var(--text-muted)",
                            "&.Mui-checked": { color: "var(--accent)" },
                          }}
                          checked={selectedToAdd.has(id)}
                          onChange={() => togglePick(id)}
                        />
                        <div className={styles.dialogRowLabel}>
                          <span className={styles.dialogRowCode}>{clientRowLabel(c)}</span>
                          <span className={styles.dialogRowMeta}>ID {id}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {filteredAvailable.length > pickerListCap ? (
                <div className={styles.dialogListFooter}>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="medium"
                    onClick={() => setPickerListCap((n) => n + PICKER_PAGE_SIZE)}
                    className={styles.addDialogLoadMore}
                  >
                    Load more ({filteredAvailable.length - pickerListCap} remaining)
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </DialogContent>

        <DialogActions className={styles.addDialogActions}>
          <Button
            type="button"
            onClick={closeAdd}
            disabled={isAdding}
            className={styles.addDialogBtnSecondary}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="contained"
            onClick={handleConfirmAdd}
            disabled={isAdding || selectedToAdd.size === 0}
            className={styles.addDialogBtnPrimary}
          >
            {isAdding ? "Adding…" : `Add selected (${selectedToAdd.size})`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteBasketOpen} onClose={isDeletingBasket ? undefined : () => setDeleteBasketOpen(false)}>
        <DialogTitle sx={{ fontFamily: "Inter, sans-serif", fontWeight: 600 }}>Delete this basket?</DialogTitle>
        <DialogContent>
          <p style={{ fontFamily: "Inter, sans-serif", fontSize: 14, color: "var(--text-default)", margin: 0 }}>
            This will permanently remove &ldquo;{basket.basket_name}&rdquo; and its client links. This cannot be undone.
          </p>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteBasketOpen(false)} disabled={isDeletingBasket} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteBasket}
            disabled={isDeletingBasket}
            sx={{ textTransform: "none" }}
          >
            {isDeletingBasket ? "Deleting…" : "Delete basket"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
